import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, PawPrint } from "lucide-react";
import { Button } from "@/components/ui/button";
import ReactMarkdown from "react-markdown";

/*
 * =============================================
 *  CONFIGURACIÓN DEL CHAT - API
 * =============================================
 *  API_KEY es la DEMO_API_KEY pública de la demo.
 *  Es intencionalmente visible — actúa como
 *  contraseña de entrada al endpoint /chat.
 *  La clave real de OpenAI vive solo en el servidor.
 * =============================================
 */
const CHAT_API_URL = "https://api.webnerstudio.com/chat";
const API_KEY = "sk-svcacct-Un0mSCxqiUkKa5RAdV0m3Vabq5P3sEEd5iI-mpvou-sM56JAtSdeZpg8F3DwA";

const INITIAL_BOT_MESSAGE = {
  id: "initial",
  role: "bot",
  text: "Hola 👋 Soy Luna, la asistente de la clínica. ¿En qué puedo ayudarte?",
  timestamp: new Date(),
};

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [messages, setMessages] = useState([INITIAL_BOT_MESSAGE]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isTyping && isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isTyping, isOpen]);

  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim() || isTyping) return;

      const userMsg = {
        id: Date.now().toString(),
        role: "user",
        text: text.trim(),
        timestamp: new Date(),
      };

      // Añadimos el mensaje del usuario al estado antes de hacer la llamada
      setMessages((prev) => [...prev, userMsg]);
      setInputValue("");
      setIsTyping(true);

      try {
        // Construimos el historial completo para enviarlo al backend.
        // Convertimos el formato interno { role: "bot"|"user", text }
        // al formato que espera OpenAI: { role: "assistant"|"user", content }
        // Excluimos el mensaje inicial del bot (es decorativo, no parte del contexto real)
        // e incluimos el mensaje actual del usuario al final.
        const history = [
          ...messages
            .filter((m) => m.id !== "initial" && !m.isError)
            .map((m) => ({
              role: m.role === "bot" ? "assistant" : "user",
              content: m.text,
            })),
          { role: "user", content: text.trim() },
        ];

        const headers = {
          "Content-Type": "application/json",
        };
        if (API_KEY) headers["X-API-DEMO-KEY"] = API_KEY;

        const response = await fetch(CHAT_API_URL, {
          method: "POST",
          headers,
          body: JSON.stringify({ messages: history }),
        });

        if (!response.ok) throw new Error("API error");

        const data = await response.json();

        const botMsg = {
          id: (Date.now() + 1).toString(),
          role: "bot",
          text: data.reply || "No he podido procesar tu mensaje.",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, botMsg]);
      } catch {
        const errorMsg = {
          id: (Date.now() + 1).toString(),
          role: "bot",
          text: "Ahora mismo no puedo responder, prueba de nuevo en 10 segundos.",
          timestamp: new Date(),
          isError: true,
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsTyping(false);
      }
    },
    [isTyping, messages]
  );

  // Listen for open-chat events from other components
  useEffect(() => {
    const handler = (e) => {
      setIsOpen(true);
      if (e.detail?.message) {
        // Small delay so the widget opens first
        setTimeout(() => {
          sendMessage(e.detail.message);
        }, 400);
      }
    };
    window.addEventListener("open-chat", handler);
    return () => window.removeEventListener("open-chat", handler);
  }, [sendMessage]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const formatTime = (date) => {
    return new Intl.DateTimeFormat("es", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  return (
    <>
      {/* Chat toggle button */}
      {!isOpen && (
        <button
          data-testid="chat-toggle-btn"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-5 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-xl hover:shadow-2xl flex items-center justify-center animate-pulse-soft hover:scale-105 transition-transform"
          aria-label="Abrir chat"
        >
          <MessageCircle className="w-6 h-6" strokeWidth={1.5} />
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div
          data-testid="chat-window"
          className="fixed bottom-20 right-5 z-50 w-[360px] max-w-[calc(100vw-2.5rem)] rounded-2xl shadow-2xl border border-border/50 bg-white overflow-hidden animate-fade-in flex flex-col"
          style={{ height: "520px", maxHeight: "calc(100vh - 120px)" }}
        >
          {/* Header */}
          <div
            data-testid="chat-header"
            className="bg-primary px-5 py-4 flex items-center justify-between shrink-0"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                <PawPrint className="w-5 h-5 text-primary-foreground" strokeWidth={2} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-primary-foreground">
                  Luna 🌙
                </h3>
                <p className="text-xs text-primary-foreground/70">
                  {isTyping ? "Escribiendo..." : "Asistente de la clínica"}
                </p>
              </div>
            </div>
            <button
              data-testid="chat-close-btn"
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors"
              aria-label="Cerrar chat"
            >
              <X className="w-4 h-4 text-primary-foreground" strokeWidth={2} />
            </button>
          </div>

          {/* Messages */}
          <div
            data-testid="chat-messages"
            className="flex-1 overflow-y-auto p-4 space-y-3 chat-scrollbar"
            style={{ background: "#FAFAF8" }}
          >
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`chat-bubble-in flex ${
                  msg.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground rounded-2xl rounded-br-md"
                      : msg.isError
                      ? "bg-red-50 text-red-700 border border-red-200 rounded-2xl rounded-bl-md"
                      : "bg-white text-foreground border border-border/50 rounded-2xl rounded-bl-md shadow-sm"
                  }`}
                >
                  <ReactMarkdown
                    disallowedElements={["script", "iframe", "object", "embed", "form", "input"]}
                    unwrapDisallowed
                    className="prose prose-sm max-w-none
                      [&_p]:my-0 [&_p]:leading-snug
                      [&_p+p]:mt-2
                      [&_ul]:mt-1 [&_ul]:mb-0 [&_ul]:pl-4
                      [&_ol]:mt-1 [&_ol]:mb-0 [&_ol]:pl-4
                      [&_li]:my-0 [&_li]:py-0 [&_li]:leading-snug
                      [&_li>p]:my-0 [&_li>p]:py-0
                      [&_li+li]:mt-0
                      [&_strong]:font-semibold"
                  >
                    {msg.text}
                  </ReactMarkdown>
                  <span
                    className={`text-[10px] mt-1 block ${
                      msg.role === "user"
                        ? "text-primary-foreground/60"
                        : "text-muted-foreground"
                    }`}
                  >
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start chat-bubble-in">
                <div className="bg-white border border-border/50 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm flex items-center gap-1.5">
                  <span className="typing-dot w-2 h-2 rounded-full bg-primary/50" />
                  <span className="typing-dot w-2 h-2 rounded-full bg-primary/50" />
                  <span className="typing-dot w-2 h-2 rounded-full bg-primary/50" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <form
            onSubmit={handleSubmit}
            data-testid="chat-input-form"
            className="shrink-0 border-t border-border/50 px-4 py-3 bg-white flex items-center gap-2"
          >
            <input
              ref={inputRef}
              data-testid="chat-input"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Escribe tu mensaje..."
              disabled={isTyping}
              maxLength={500}
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none disabled:opacity-50"
            />
            <Button
              type="submit"
              data-testid="chat-send-btn"
              disabled={!inputValue.trim() || isTyping}
              size="icon"
              className="w-9 h-9 rounded-full bg-primary text-primary-foreground hover:bg-[#1e3d22] shrink-0 disabled:opacity-40"
            >
              <Send className="w-4 h-4" strokeWidth={2} />
            </Button>
          </form>
        </div>
      )}
    </>
  );
};
